import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowUpRight, Check, CheckCircle2 } from "lucide-react";
import { scrollToId } from "@/lib/scroll";
import { CONTACT } from "@/lib/contact";
import { openAskbox, useAskbox } from "@/lib/askbox";

const EASE = [0.16, 1, 0.3, 1];

const inputCls =
    "w-full bg-[#0E121B] border border-white/10 rounded-xl px-4 py-3.5 text-sm text-white placeholder:text-slate-500 outline-none focus:border-[#FF3B30]/60 focus:ring-2 focus:ring-[#FF3B30]/20 transition-[border-color,box-shadow] duration-300";

const steps = [
    {
        step: "01",
        title: "Send the site URL",
        copy: "Point us at the website you already have, plus any FAQ doc you want in the answers.",
    },
    {
        step: "02",
        title: "We build it in 72 hours",
        copy: "The widget is trained on your pages. If it is not live in 72 hours, it is not done.",
    },
    {
        step: "03",
        title: "Paste one snippet",
        copy: "You paste it, or grant WordPress / Shopify access and we paste it.",
    },
];

const included = [
    "Embeddable Askbox widget",
    "Answer brain built from your existing pages",
    "Next-step buttons: book, buy, or leave a number",
    "7-day tweak window after launch",
];

export default function AskboxPage() {
    const [submitted, setSubmitted] = useState(false);
    const [form, setForm] = useState({
        name: "",
        email: "",
        business: "",
        phone: "",
        website: "",
    });

    useAskbox({
        brain: "/widgets/askbox/brain.json",
        desktopMount: "#askbox-hero-slot",
        openDesktop: true,
    });

    useEffect(() => {
        document.title = "Askbox — FAQ widget for an existing site | SalesCan";
        return () => {
            document.title = "SalesCan";
        };
    }, []);

    const onSubmit = (e) => {
        e.preventDefault();
        const body = [
            `Name: ${form.name}`,
            `Email: ${form.email}`,
            `Business: ${form.business}`,
            `Phone: ${form.phone}`,
            `Website: ${form.website}`,
        ].join("\n");
        window.location.href = `mailto:${CONTACT.email}?subject=${encodeURIComponent(
            "Askbox — get this on my site"
        )}&body=${encodeURIComponent(body)}`;
        setSubmitted(true);
    };

    return (
        <main data-testid="askbox-page">
            <section className="relative flex items-center px-4 sm:px-8 lg:px-16 pt-28 pb-16 overflow-hidden">
                <div className="absolute inset-0 bg-[radial-gradient(ellipse_80%_60%_at_50%_-10%,rgba(255,59,48,0.12),transparent)] pointer-events-none" />
                <div className="relative max-w-[1400px] mx-auto w-full grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16 items-center">
                    <motion.div
                        initial={{ opacity: 0, y: 32 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.8, ease: EASE }}
                    >
                        <p className="font-mono-tech text-xs tracking-[0.2em] uppercase text-[#FF3B30] font-semibold mb-4">
                            Askbox · SalesCan
                        </p>
                        <h1 className="font-display font-bold text-3xl sm:text-4xl lg:text-[2.75rem] text-white leading-[1.12] tracking-tight">
                            Every question on your site gets an answer and a next step.
                        </h1>
                        <p className="mt-5 text-base sm:text-lg text-slate-300 max-w-xl leading-relaxed">
                            Answers from the pages you already have. Visitors can book, buy, or leave
                            a number. Live in 72 hours.
                        </p>
                        <p className="mt-4 text-sm text-slate-500">
                            This page is answering from this page. Ask it.
                        </p>
                        <div className="mt-8 flex flex-col sm:flex-row sm:items-center gap-4">
                            <button
                                type="button"
                                data-testid="askbox-ask-cta"
                                onClick={openAskbox}
                                className="inline-flex items-center justify-center gap-2 bg-[#FF3B30] hover:bg-[#ff5147] text-white font-semibold px-6 py-3.5 rounded-full transition-colors duration-300"
                            >
                                Ask it something
                            </button>
                            <button
                                type="button"
                                data-testid="askbox-get-cta"
                                onClick={() => scrollToId("#get-started")}
                                className="inline-flex items-center justify-center gap-2 border border-white/15 hover:border-white/30 text-slate-200 font-semibold px-6 py-3.5 rounded-full transition-colors duration-300"
                            >
                                Get this on your site
                            </button>
                        </div>
                        <Link
                            to="/askbox/demo"
                            data-testid="askbox-clinic-link"
                            className="mt-5 inline-flex items-center gap-1 text-sm text-slate-400 hover:text-white underline underline-offset-4 decoration-white/20"
                        >
                            See it on a fake clinic
                            <ArrowUpRight className="w-3.5 h-3.5" />
                        </Link>
                    </motion.div>
                    <div
                        id="askbox-hero-slot"
                        data-testid="askbox-hero-widget"
                        className="hidden lg:block min-h-[560px]"
                    />
                </div>
            </section>

            <section className="px-4 sm:px-8 lg:px-16 py-16 lg:py-20 border-t border-white/5">
                <div className="max-w-[1400px] mx-auto">
                    <p className="font-mono-tech text-xs uppercase tracking-[0.2em] text-[#FF3B30] font-semibold mb-3">
                        How it works
                    </p>
                    <h2 className="font-display font-bold text-2xl sm:text-3xl text-white mb-10">
                        Three steps. Then it lives on your site.
                    </h2>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
                        {steps.map((item) => (
                            <div
                                key={item.step}
                                className="rounded-2xl border border-white/10 bg-[#0E121B]/60 backdrop-blur p-6"
                            >
                                <p className="font-mono-tech text-xs text-[#FF3B30] tracking-[0.25em] mb-3">
                                    {item.step}
                                </p>
                                <h3 className="font-display font-semibold text-lg text-white mb-2">
                                    {item.title}
                                </h3>
                                <p className="text-sm text-slate-400 leading-relaxed">{item.copy}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            <section className="px-4 sm:px-8 lg:px-16 py-16 lg:py-20 bg-[#0E121B]/40">
                <div className="max-w-[1400px] mx-auto grid grid-cols-1 lg:grid-cols-12 gap-10">
                    <div className="lg:col-span-5">
                        <p className="font-mono-tech text-xs uppercase tracking-[0.2em] text-[#FF3B30] font-semibold mb-3">
                            What’s included
                        </p>
                        <h2 className="font-display font-bold text-2xl sm:text-3xl text-white leading-snug">
                            The widget, the answers, and a week to tighten them.
                        </h2>
                    </div>
                    <ul className="lg:col-span-7 space-y-3">
                        {included.map((item) => (
                            <li key={item} className="flex items-start gap-3 text-sm text-slate-300">
                                <Check className="w-4 h-4 text-[#FF3B30] shrink-0 mt-0.5" />
                                {item}
                            </li>
                        ))}
                        <li className="flex items-start gap-3 text-sm text-slate-500 pt-2">
                            <Check className="w-4 h-4 text-slate-600 shrink-0 mt-0.5" />
                            Optional keep-alive: monthly refresh, transcript review, and hosting. Not
                            required to launch.
                        </li>
                    </ul>
                </div>
            </section>

            <section className="px-4 sm:px-8 lg:px-16 py-16 lg:py-20">
                <div className="max-w-[1400px] mx-auto">
                    <div className="max-w-[720px]">
                        <p className="font-mono-tech text-xs uppercase tracking-[0.2em] text-[#FF3B30] font-semibold mb-3">
                            Price
                        </p>
                        <h2 className="font-display font-bold text-2xl sm:text-3xl text-white">
                            $1,197 CAD setup. Keep-alive is optional.
                        </h2>
                        <p className="mt-4 text-slate-300 leading-relaxed">
                            Setup is one-time. Live in 72 hours from a complete URL.
                        </p>
                        <p className="mt-3 text-slate-400 leading-relaxed">
                            Optional keep-alive is $1,197 CAD/month for hosting, a monthly refresh, and
                            transcript review. You can launch on setup only.
                        </p>
                        <p className="mt-6 text-sm text-slate-500">
                            Prefer Fiverr for buyer protection:{" "}
                            <a
                                href="https://www.fiverr.com/will1557/build-an-ai-chatbot-faq-bot-for-your-website-in-72-hours"
                                className="text-slate-300 hover:text-white underline underline-offset-4 decoration-white/20"
                                target="_blank"
                                rel="noreferrer"
                            >
                                order Askbox on Fiverr
                            </a>
                            .
                        </p>
                        <p className="mt-3 text-xs text-slate-600">
                            Already decided?{" "}
                            <a
                                href="https://buy.stripe.com/14A8wRc5o5g34SYes7e3e00"
                                className="hover:text-slate-400 underline underline-offset-4 decoration-white/10"
                                target="_blank"
                                rel="noreferrer"
                            >
                                Stripe checkout
                            </a>
                            .
                        </p>
                    </div>
                </div>
            </section>

            <section
                id="get-started"
                data-testid="askbox-intake-section"
                className="relative px-4 sm:px-8 lg:px-16 pt-20 pb-28 lg:py-28 overflow-hidden border-t border-white/5"
            >
                <div className="relative max-w-[1400px] mx-auto grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
                    <div className="lg:col-span-5">
                        <p className="font-mono-tech text-xs uppercase tracking-[0.2em] text-[#FF3B30] font-semibold mb-3">
                            Get started
                        </p>
                        <h2 className="font-display font-bold text-2xl sm:text-3xl text-white leading-snug">
                            Get Askbox on the site you already have.
                        </h2>
                        <p className="mt-4 text-slate-400 leading-relaxed">
                            Send the URL. We build from your pages. You paste one snippet, or we paste
                            it.
                        </p>
                    </div>
                    <div className="lg:col-span-7">
                        <div className="rounded-3xl border border-white/10 bg-[#0E121B]/75 backdrop-blur-2xl p-8 sm:p-10">
                            {submitted ? (
                                <div data-testid="askbox-form-success" className="py-12 text-center">
                                    <CheckCircle2 className="w-12 h-12 text-[#FF3B30] mx-auto mb-5" />
                                    <h3 className="font-display font-bold text-2xl text-white">
                                        Your email app should open
                                    </h3>
                                    <p className="mt-3 text-sm text-slate-400 max-w-sm mx-auto leading-relaxed">
                                        If it doesn’t, email {CONTACT.email} with your site URL.
                                    </p>
                                </div>
                            ) : (
                                <form onSubmit={onSubmit} className="space-y-5">
                                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                                        <div>
                                            <label
                                                htmlFor="askbox-name"
                                                className="block font-mono-tech text-xs uppercase tracking-[0.2em] text-slate-500 mb-2"
                                            >
                                                Name
                                            </label>
                                            <input
                                                id="askbox-name"
                                                required
                                                value={form.name}
                                                onChange={(e) =>
                                                    setForm({ ...form, name: e.target.value })
                                                }
                                                placeholder="Jane Tremblay"
                                                className={inputCls}
                                            />
                                        </div>
                                        <div>
                                            <label
                                                htmlFor="askbox-email"
                                                className="block font-mono-tech text-xs uppercase tracking-[0.2em] text-slate-500 mb-2"
                                            >
                                                Email
                                            </label>
                                            <input
                                                id="askbox-email"
                                                type="email"
                                                required
                                                value={form.email}
                                                onChange={(e) =>
                                                    setForm({ ...form, email: e.target.value })
                                                }
                                                placeholder="jane@business.ca"
                                                className={inputCls}
                                            />
                                        </div>
                                    </div>
                                    <div>
                                        <label
                                            htmlFor="askbox-business"
                                            className="block font-mono-tech text-xs uppercase tracking-[0.2em] text-slate-500 mb-2"
                                        >
                                            Business name
                                        </label>
                                        <input
                                            id="askbox-business"
                                            required
                                            value={form.business}
                                            onChange={(e) =>
                                                setForm({ ...form, business: e.target.value })
                                            }
                                            placeholder="Acme Clinic"
                                            className={inputCls}
                                        />
                                    </div>
                                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                                        <div>
                                            <label
                                                htmlFor="askbox-phone"
                                                className="block font-mono-tech text-xs uppercase tracking-[0.2em] text-slate-500 mb-2"
                                            >
                                                Phone
                                            </label>
                                            <input
                                                id="askbox-phone"
                                                type="tel"
                                                required
                                                value={form.phone}
                                                onChange={(e) =>
                                                    setForm({ ...form, phone: e.target.value })
                                                }
                                                placeholder="(905) 555-0100"
                                                className={inputCls}
                                            />
                                        </div>
                                        <div>
                                            <label
                                                htmlFor="askbox-url"
                                                className="block font-mono-tech text-xs uppercase tracking-[0.2em] text-slate-500 mb-2"
                                            >
                                                Website URL
                                            </label>
                                            <input
                                                id="askbox-url"
                                                type="url"
                                                required
                                                value={form.website}
                                                onChange={(e) =>
                                                    setForm({ ...form, website: e.target.value })
                                                }
                                                placeholder="https://yoursite.ca"
                                                className={inputCls}
                                            />
                                        </div>
                                    </div>
                                    <button
                                        type="submit"
                                        data-testid="askbox-form-submit"
                                        className="w-full flex items-center justify-center gap-2 bg-[#FF3B30] hover:bg-[#ff5147] text-white font-semibold px-7 py-4 rounded-full transition-colors duration-300"
                                    >
                                        Get Askbox on this site
                                        <ArrowUpRight className="w-5 h-5" />
                                    </button>
                                    <p className="text-center text-xs text-slate-500">
                                        $1,197 CAD setup, paid before build. 72 hours from a complete
                                        URL.
                                    </p>
                                </form>
                            )}
                        </div>
                    </div>
                </div>
            </section>
        </main>
    );
}
